// Date utilities used across GSec pages

/**
 * Parse a yyyy-MM-dd string as a local date (not UTC)
 * @param {string} dateStr - Date string in yyyy-MM-dd format
 * @returns {Date|null} Parsed Date or null if invalid
 */
export function parseLocalYMD(dateStr) {
  if (!dateStr) return null;
  const match = /^([0-9]{4})-([0-9]{2})-([0-9]{2})$/.exec(dateStr);
  if (!match) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

/**
 * Format a Date (or parseable string) as yyyy-MM-dd (local)
 * Keeps existing behavior used by pricing code.
 * @param {Date|string} date
 * @returns {string}
 */
export function formatLocalYMD(date) {
  if (!date) return '';
  if (typeof date === 'string') {
    // If already yyyy-MM-dd, return as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) return date;
    date = new Date(date);
  }
  if (!(date instanceof Date) || isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// Coupon Schedule and Date Utilities

/**
 * Generate full coupon schedule for semi-annual coupons provided
 * two coupon day-month strings (MM-DD)
 * @param {string|Date} issueDate
 * @param {string|Date} maturityDate
 * @param {string} couponDate1 - MM-DD
 * @param {string} couponDate2 - MM-DD
 * @returns {Date[]}
 */
export function generateFullCouponSchedule(issueDate, maturityDate, couponDate1, couponDate2) {
  if (!issueDate || !maturityDate || !couponDate1 || !couponDate2) return [];
  const schedule = [];
  const start = new Date(issueDate);
  const end = new Date(maturityDate);
  let year = start.getFullYear();

  // Parse coupon MM-DD (e.g., '04-15')
  const [m1, d1] = couponDate1.split('-').map(Number);
  const [m2, d2] = couponDate2.split('-').map(Number);

  while (true) {
    const c1 = new Date(year, m1 - 1, d1);
    const c2 = new Date(year, m2 - 1, d2);
    if (c1 >= start && c1 <= end) schedule.push(new Date(c1));
    if (c2 >= start && c2 <= end) schedule.push(new Date(c2));
    if (c1 > end && c2 > end) break;
    year++;
  }
  schedule.sort((a, b) => a - b);
  return schedule;
}

/**
 * Find last and next coupon dates relative to a valueDate
 * @param {Date[]} schedule
 * @param {Date|string} valueDate
 * @returns {{lastCouponDate: Date|null, nextCouponDate: Date|null}}
 */
export function getLastAndNextCouponDates(schedule, valueDate) {
  if (!Array.isArray(schedule) || schedule.length === 0 || !valueDate) {
    return { lastCouponDate: null, nextCouponDate: null };
  }
  const vDate = new Date(valueDate);
  let last = null, next = null;
  for (let i = 0; i < schedule.length; i++) {
    if (schedule[i] <= vDate) last = schedule[i];
    if (schedule[i] > vDate) { next = schedule[i]; break; }
  }
  return { lastCouponDate: last, nextCouponDate: next };
}

/**
 * Days difference helper (absolute days, rounded)
 * Keeps prior behavior relied on by pricing code.
 * @param {Date|string} date1
 * @param {Date|string} date2
 * @returns {number}
 */
export function getDaysDifference(date1, date2) {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  // Use UTC midnight so day differences are not affected by timezone/DST shifts.
  const utc1 = Date.UTC(d1.getFullYear(), d1.getMonth(), d1.getDate());
  const utc2 = Date.UTC(d2.getFullYear(), d2.getMonth(), d2.getDate());
  return Math.abs(utc1 - utc2) / (24 * 60 * 60 * 1000);
}

/**
 * Simple YYYY-MM-DD formatter from Date (local)
 * @param {Date} date
 * @returns {string}
 */
export function formatDate(date) {
  if (!(date instanceof Date) || isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Find the coupon period containing settlement by walking backward from maturity
 * (matches Microsoft Excel PRICE — no issue date).
 */
export function findCouponPeriodFromMaturity(settlement, maturity, frequency) {
  const monthsPerPeriod = 12 / frequency;
  let next = new Date(maturity);
  let prev = new Date(maturity);
  prev.setMonth(prev.getMonth() - monthsPerPeriod);
  // Strict "<" so that a settlement date landing exactly on a coupon date is
  // treated as the start of the new period (0 days accrued), not the end of
  // the previous one (full period accrued).
  while (settlement < prev) {
    next = new Date(prev);
    prev = new Date(prev);
    prev.setMonth(prev.getMonth() - monthsPerPeriod);
  }
  return { lastCoupon: prev, nextCoupon: next };
}

/**
 * Excel PRICE function implementation
 * Matches Microsoft Excel PRICE(settlement, maturity, rate, yld, redemption, frequency, [basis]).
 * Important: when N = 1 (only one coupon from settlement to maturity), Excel uses a different
 * closed form than N > 1 — see Microsoft / Tutorialspoint docs.
 *
 * Extra args (issueDate, couponDate1, couponDate2) are ignored for parity with Excel.
 */
export function excelPRICE(
  settlement,
  maturity,
  rate,
  yld,
  redemption = 100,
  frequency = 2,
  basis = 1,
  _issueDate = null,
  _couponDate1 = null,
  _couponDate2 = null
) {
  const settle = new Date(settlement);
  const mat = new Date(maturity);

  if (isNaN(settle.getTime()) || isNaN(mat.getTime())) {
    return NaN;
  }

  if (settle >= mat) {
    return NaN;
  }

  const ratePerPeriod = rate / frequency;
  const yldPerPeriod = yld / frequency;
  const monthsPerPeriod = 12 / frequency;

  const { lastCoupon, nextCoupon } = findCouponPeriodFromMaturity(settle, mat, frequency);

  let E;
  let A;
  let DSC;

  if (basis === 1) {
    E = getDaysDifference(nextCoupon, lastCoupon);
    A = getDaysDifference(settle, lastCoupon);
    DSC = getDaysDifference(nextCoupon, settle);
  } else if (basis === 0) {
    E = 180;
    DSC = getDaysDifference360US(nextCoupon, settle);
    A = getDaysDifference360US(settle, lastCoupon);
  } else if (basis === 2) {
    E = getDaysDifference(nextCoupon, lastCoupon);
    A = getDaysDifference(settle, lastCoupon);
    DSC = getDaysDifference(nextCoupon, settle);
  } else if (basis === 3) {
    E = getDaysDifference(nextCoupon, lastCoupon);
    A = getDaysDifference(settle, lastCoupon);
    DSC = getDaysDifference(nextCoupon, settle);
  } else if (basis === 4) {
    E = 180;
    DSC = getDaysDifference360EU(nextCoupon, settle);
    A = getDaysDifference360EU(settle, lastCoupon);
  } else {
    E = getDaysDifference(nextCoupon, lastCoupon);
    A = getDaysDifference(settle, lastCoupon);
    DSC = getDaysDifference(nextCoupon, settle);
  }

  if (E === 0) return NaN;

  const couponPayment = redemption * ratePerPeriod;

  let N = 0;
  let currentDate = new Date(nextCoupon);
  while (currentDate <= mat) {
    N++;
    if (currentDate.getTime() === mat.getTime()) break;
    currentDate.setMonth(currentDate.getMonth() + monthsPerPeriod);
  }

  const dscOverE = DSC / E;

  if (N === 1) {
    const DSR = E - A;
    const T1 = redemption * ratePerPeriod + redemption;
    const T2 = yldPerPeriod * (DSR / E) + 1;
    const T3 = redemption * ratePerPeriod * (A / E);
    return T1 / T2 - T3;
  }

  const pvRedemption = redemption / Math.pow(1 + yldPerPeriod, (N - 1) + dscOverE);
  let pvCoupons = 0;
  for (let i = 1; i <= N; i++) {
    const periodNum = (i - 1) + dscOverE;
    pvCoupons += couponPayment / Math.pow(1 + yldPerPeriod, periodNum);
  }
  const accruedInterest = couponPayment * (A / E);
  return pvRedemption + pvCoupons - accruedInterest;
}

/**
 * Helper function for US 30/360 day count
 */
function getDaysDifference360US(date1, date2) {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  const year1 = d1.getFullYear();
  const month1 = d1.getMonth();
  const day1 = Math.min(d1.getDate(), 30);
  const year2 = d2.getFullYear();
  const month2 = d2.getMonth();
  const day2 = Math.min(d2.getDate(), 30);
  
  return (year2 - year1) * 360 + (month2 - month1) * 30 + (day2 - day1);
}

/**
 * Helper function for European 30/360 day count
 */
function getDaysDifference360EU(date1, date2) {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  const year1 = d1.getFullYear();
  const month1 = d1.getMonth();
  const day1 = d1.getDate() === 31 ? 30 : d1.getDate();
  const year2 = d2.getFullYear();
  const month2 = d2.getMonth();
  const day2 = d2.getDate() === 31 ? 30 : d2.getDate();
  
  return (year2 - year1) * 360 + (month2 - month1) * 30 + (day2 - day1);
}


